import type { BookImportItem, BookImportJob, BookImportKind, CoworkFile } from "./api"

export type ImportFilter = "all" | "review" | "failed" | "processing" | "cancelled"

const relatedImportKinds: Partial<Record<BookImportKind, readonly BookImportKind[]>> = {
  statement: ["statement", "payment"],
  bank_account: ["bank_account", "bank_identity"],
  payment: ["payment", "allocation", "refund"],
  customer_advance: ["customer_advance", "advance_application", "advance_refund"],
  supplier_advance: ["supplier_advance", "advance_application", "advance_refund"],
}
const documentImportKinds: Record<string, readonly BookImportKind[]> = {
  invoice: ["invoice"], receipt: ["invoice"],
  chart_of_accounts: ["account"], account_list: ["account"], accounts: ["account"],
  bank_statement: ["statement", "payment"], payment_order: ["payment"],
  accounting_journal: ["journal"], general_ledger: ["journal"],
  opening_balances: ["opening"], trial_balance: ["opening"],
  opening_items: ["opening_items"],
  account_subledger_statement: ["opening_items"], account_turnover_schedule: ["opening_items"],
  operation_journal: ["journal"],
  product_catalog: ["product"], product_list: ["product"],
  customer_list: ["counterparty"], supplier_list: ["counterparty"], counterparties: ["counterparty"],
  bank_details: ["bank_account", "bank_identity"], asset_register: ["asset"],
}
export const importTargetKinds = (target: BookImportKind) => relatedImportKinds[target] ?? [target]
const sourceKinds = (target: BookImportKind): readonly BookImportKind[] =>
  target === "customer_advance" || target === "supplier_advance" ? [target] : importTargetKinds(target)
const matchesDocumentType = (type: unknown, target: BookImportKind) => {
  if (typeof type !== "string") return false
  const normalized = type.trim().toLowerCase().replace(/[\s-]+/g, "_")
  const mapped = Object.hasOwn(documentImportKinds, normalized) ? documentImportKinds[normalized] : []
  return sourceKinds(target).some((kind) => kind === normalized || mapped.includes(kind))
}

export const importNoteLabel: Record<string, keyof typeof import("@/i18n/en").dict> = {
  "Storno: the negative source amount retains its original signed turnover. Positive reversed ledger lines preserve its balance effect. Review before posting.": "books.stornoTurnover",
  "Storno: negative source turnover; ledger lines show the positive reversed balance effect. Draft uses the printed base-currency amount; foreign-currency figures and analytics remain in the saved source facts.": "books.stornoTurnover",
  "An account mapping is required. Classify this account from the source and Company Knowledge, with a rationale, before automatic registration.": "book.import.note.accountMappingMissing",
  "The account classification remains uncertain and requires review; no account type was inferred.": "book.import.note.accountMappingReview",
  "Reused the matching active account definition; no new classification was inferred.": "book.import.note.accountMappingReuse",
  "The proposed account conflicts with the registered definition or the account is inactive. Review the mapping; registered settings were preserved.": "book.import.note.accountMappingConflict",
  "Negative source amount is represented by reversing debit and credit sides. Net balances are preserved, but gross turnovers differ from the signed source columns. Review this representation before posting.": "book.import.note.signedReversal",
  "Account classification inferred from its source name or parent definition; existing registered settings are never overwritten.": "book.import.note.accountInferred",
  "The source defines this account, but its classification requires review; no default asset type was assigned.": "book.import.note.accountUnclassified",
  "Draft derived from validated trial-balance facts. Review the currency basis and whether the underlying activity is already recorded before posting; closing balances are reconciliation only.": "book.import.note.trialBalanceDerived",
  "Beginning-of-year opening balances; review the source range before posting in the selected period.": "book.import.note.trialBalanceOpening",
  "Year-to-date turnover from 1 January through the report end; not Q3-only activity. Post only in a period spanning the full source range.": "book.import.note.trialBalanceTurnover",
  "Select an accounting period before creating this entry": "book.import.note.selectPeriod",
  "Company settings changed after this proposal; refresh the mapping before creating entries": "book.import.note.settingsChanged",
  "An existing imported entry covers this source; review before creating another": "book.import.note.existingEntry",
  "An imported invoice needs its source document number": "book.import.note.invoiceNumber",
  "Extracted fields need review": "book.import.note.fieldsReview",
}

export function matchesImportTarget(job: BookImportJob, target: BookImportKind = "auto") {
  if (target === "auto" || job.target === target) return true
  if (["opening", "journal"].includes(target) && job.items.some(item => ["closing_control", "trial_balance_review"].includes(String(item.data.source_section)))) return true
  // Match actual entry kinds, not supporting fields such as an invoice's account_code.
  if (job.items.some((item) => sourceKinds(target).includes(item.kind))) return true
  return job.target === "auto" && matchesDocumentType(job.document_summary?.document_type, target)
}

export function matchesUnprocessedImportTarget(file: CoworkFile, target: BookImportKind = "auto") {
  if (target === "auto") return true
  const metadata = file.parsed_metadata
  const linked = metadata.book_import as { target?: unknown } | undefined
  if (linked?.target === target) return true
  const summary = metadata.document_summary as { document_type?: unknown } | undefined
  return matchesDocumentType(summary?.document_type ?? metadata.document_type, target)
}

type ImportStatus = Pick<BookImportJob, "status" | "error_code" | "completeness" | "overlapping_import_basis" | "extraction_status" | "extraction_quality_status">
export function importDisplayStatus(job: ImportStatus): BookImportJob["status"] {
  // The worker returns review for these stopped attempts. Classify their display
  // as failed without changing the underlying retry/recovery workflow state.
  if (job.status === "review" && ["no_saved_extraction", "provider_rate_limited"].includes(job.error_code ?? "")) return "failed"
  if (job.status === "completed" && (job.extraction_status === "partial" || job.extraction_quality_status === "accepted_with_warnings" ||
    job.overlapping_import_basis || job.completeness?.source_partial || job.completeness?.source_warnings ||
    job.completeness?.unresolved_rows || job.completeness?.memorandum_reviews)) return "review"
  return job.status
}

export function matchesImportFilter(job: ImportStatus, filter: ImportFilter) {
  const status = importDisplayStatus(job)
  if (filter === "all") return true
  if (filter === "processing") return ["queued", "dispatching", "running", "applying"].includes(status)
  if (filter === "review") return ["review", "submission_unknown"].includes(status)
  return status === filter
}

export type ImportScope = { companyID: string; projectID: string; periodID?: string | null }
export const importScopeKey = (scope: ImportScope) => JSON.stringify([scope.companyID, scope.projectID, scope.periodID ?? null])
export const importText = (data: Record<string, unknown>, key: string, fallback = "") => {
  const value = data[key]
  if (typeof value === "string") return value.startsWith("@") && (key.endsWith("_id") || key.endsWith("_code")) ? fallback : value
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER ? String(value) : fallback
}
export const importRows = (data: Record<string, unknown>) => Array.isArray(data.lines)
  ? data.lines.slice(0, 100).filter((row): row is Record<string, unknown> => !!row && typeof row === "object" && !Array.isArray(row)) : []

export function closingControlRows(data: Record<string, unknown>) {
  if (!data.leaves || typeof data.leaves !== "object" || Array.isArray(data.leaves)) return []
  return Object.entries(data.leaves).slice(0, 200)
    .filter((entry): entry is [string, Record<string, unknown>] => !!entry[1] && typeof entry[1] === "object" && !Array.isArray(entry[1]))
    .map(([code, row]) => ({ code, name: importText(row, "name"), debit: importText(row, "closing_debit"), credit: importText(row, "closing_credit") }))
}

export const importKindLabel: Record<BookImportKind, keyof typeof import("@/i18n/en").dict> = {
  auto: "book.import.auto", invoice: "billing.title", counterparty: "billing.parties", journal: "books.journals",
  opening: "books.opening", opening_items: "financial.openingItems", account: "books.accounts", bank_account: "cowork.setup.money", bank_identity: "books.bank",
  payment: "payments.title", statement: "payments.statements", credit_note: "billing.credits",
  prepayment_invoice: "prepayment.title", customer_advance: "advances.title", supplier_advance: "supplierAdvances.title",
  allocation: "payments.allocate", refund: "payments.refund", advance_application: "advances.apply", advance_refund: "advances.refund",
  product: "catalog.title", invoice_series: "series.title",
  dimension: "dimensions.title",
  asset: "assets.title",
}

export function importItemStatusLabel(item: BookImportItem): keyof typeof import("@/i18n/en").dict {
  if (item.data.source_section === "closing_control") return "books.trialControlStatus"
  if (["journal", "opening"].includes(item.kind) && item.record_id) {
    if (item.record_status === "voided") return "books.voided"
    if (item.record_status === "deleted") return "books.deletedDraft"
    if (item.record_status === "draft") return "books.draft"
    if (item.record_status === "posted") return "books.posted"
  }
  if (item.kind === "payment" && item.record_id) {
    if (item.record_status === "cancelled") return "book.import.cancelled"
    if (item.record_status === "draft") return "billing.draft"
    if (item.record_status === "confirmed") return "billing.confirmed"
  }
  return item.status === "draft" ? "billing.draft" : `book.import.${item.status}`
}

export function importedRecordRoute(item: BookImportItem): string | undefined {
  const id = item.record_id ? encodeURIComponent(item.record_id) : undefined
  if (!id || item.record_status === "deleted") return
  if (item.kind === "invoice") return `/billing?view=${item.data.invoice_type === "sale" ? "sales" : "purchases"}&invoice=${id}`
  if (item.kind === "opening_items") return `/financial-reports?view=aging&opening_batch=${id}`
  if (item.kind === "opening" || item.kind === "journal" && item.data.entry_type === "opening") return `/books?view=opening&journal=${id}`
  if (item.kind === "journal") return `/books?journal=${id}`
  if (item.kind === "counterparty") return "/billing?view=counterparties"
  if (item.kind === "credit_note") return "/billing?view=credits"
  if (item.kind === "prepayment_invoice") return "/billing?view=prepayments"
  if (item.kind === "payment") return `/payments?direction=all${item.record_status === "cancelled" ? "&status=cancelled" : ""}`
  if (item.kind === "product") return "/billing?view=products"
  if (item.kind === "account") return "/settings/accounts"
  if (["bank_account", "bank_identity"].includes(item.kind)) return "/books?view=money"
}

export type ImportReviewHandoff = { jobID: string; itemID: string; companyID: string; projectID: string; periodID: string | null }

export function importReviewHandoff(state: unknown): ImportReviewHandoff | undefined {
  if (!state || typeof state !== "object" || !("importReview" in state)) return
  const value = state.importReview
  if (!value || typeof value !== "object") return
  const handoff = value as Partial<ImportReviewHandoff>
  if (![handoff.jobID, handoff.itemID, handoff.companyID, handoff.projectID].every((part) =>
    typeof part === "string" && part.length > 0 && part.length <= 128) ||
    (handoff.periodID !== null && (typeof handoff.periodID !== "string" || handoff.periodID.length > 128))) return
  return handoff as ImportReviewHandoff
}

export function reviewImportRoute(item: BookImportItem, target?: BookImportKind): string | undefined {
  switch (item.kind) {
    case "invoice": return item.data.invoice_type === "sale" ? "/billing?view=sales" :
      item.data.invoice_type === "purchase" ? "/billing?view=purchases" : undefined
    case "prepayment_invoice": return "/billing?view=prepayments"
    case "credit_note": return "/billing?view=credits"
    case "counterparty": return "/billing?view=counterparties"
    case "product": return "/billing?view=products"
    case "journal": return "/books"
    case "opening": return "/books?view=opening"
    case "asset": return "/books?view=assets"
    case "account": return "/settings/accounts"
    case "bank_account":
    case "bank_identity": return "/books?view=money"
    case "dimension": return "/books?view=dimensions"
    case "invoice_series": return "/settings/company"
    case "payment": return `/payments?direction=${item.data.direction === "outgoing" ? "outgoing" : "incoming"}`
    case "allocation":
    case "refund": return "/payments?direction=all"
    case "customer_advance": return "/payments?view=advances"
    case "supplier_advance": return "/payments?view=supplier-advances"
    case "advance_application":
    case "advance_refund": return target === "supplier_advance" ? "/payments?view=supplier-advances" :
      target === "customer_advance" ? "/payments?view=advances" : undefined
    default: return undefined
  }
}
